import {referenceNumberForWire} from './billing-entry-view';
import {paymentMethodDetailsError} from './payment-method-details';
/**
 * SMART-PAYMENT-DRAWER-V1 -- the collect drawer's PURE logic (Refs #315).
 *
 * React-free: prefill derivation, field-level validation, and the submit
 * state machine for the ONE focused "Collect payment" drawer opened in place
 * from a day-board row or a billing-queue row.
 *
 * REUSE-BEFORE-BUILD (binding -- re-deriving any of these is a package
 * failure):
 *   - MONEY TRUTH is the server's. This module DISPLAYS the checkout-handoff
 *     wire's own numbers (outstanding / collected / accepted) and performs NO
 *     netting. The only arithmetic here is the operator's raw amount string
 *     -> minor units, through the EXISTING parseInrToMinorUnits (string math;
 *     a float never touches money). A second parser is forbidden.
 *   - EPISODE IDENTITY is `handoff.doorAppointmentId` VERBATIM (LES-022 -- a
 *     door never re-picks its episode). The drawer never re-derives which
 *     visit it is collecting for.
 *   - THE PREFILL AMOUNT is `handoff.doorSuggestedAmountInr` VERBATIM. That
 *     field is ALREADY whole-rupees-or-null by construction in
 *     lib/billing-queue.ts (`outstanding % 100 === 0 ? outstanding / 100 :
 *     null`), so the whole-rupee-only rule needs NO client-side test -- a
 *     paise-bearing outstanding and a `not_configured` episode BOTH arrive as
 *     null and seed NO amount. Never Rs 0 (the NULL-AMOUNT OBLIGATION LAW).
 *
 * V1 IS VISIT-ANCHORED ONLY (founder ruling 2026-07-28): an appointment id is
 * required. Patient-anchored payment stays in Full Billing. Exactly ONE
 * allocation per submission -- there is no split-tender state here by design.
 */

import {
  formatMinorUnitsInr,
  parseInrToMinorUnits,
  occurredAtIsoFromLocalInput,
  ACCEPTED_AMOUNT_NOT_CONFIGURED_COPY,
  episodeStateLabel,
  type BillingQueueEpisodeState,
} from '@/dashboard/billing/billing-entry-view';
import type { CheckoutHandoffResponse } from '@/dashboard/billing/checkout-handoff-view';
import { collectionOutstandingMinorUnits } from '@/dashboard/billing/checkout-handoff-view';

// ---- Form state -----------------------------------------------------------

/**
 * USER INPUTS ONLY (scope E). No note, no doctor field, no category, no print
 * control, no split control -- each is a registered deferral with a named
 * carrier, never a silently-resolved gap.
 *
 * THE REFERENCE TOKEN IS NO LONGER DEFERRED (L-03 / R-14): the operator types
 * the UPI ref, cheque no or txn id HERE, where she collects, instead of
 * leaving the drawer for Full Billing. Ravi’s 16 October payment-mode contract requires a reference for every
 * non-Cash collection. The shared method contract validates it before arming;
 * the atomic writer independently enforces the same received-money rules.
 */
export interface PaymentDrawerForm {
  /** Raw rupee input (the operator's string; parsed, never floated). */
  readonly amountRaw: string;
  /** Controlled token from PAYMENT_METHOD_OPTIONS; '' = unpicked. */
  readonly method: string;
  /** Local datetime-input value ('YYYY-MM-DDTHH:MM'). */
  readonly receivedAtLocal: string;
  /** Raw reference token (OPTIONAL). '' = not recorded -- never sent as ''. */
  readonly referenceRaw: string;
  readonly methodSubtype?: string;
  readonly methodOtherName?: string;
}

/** The fields an error may anchor to. A page-level banner is FORBIDDEN where
 *  one of these can explain the failure (closes gap.f-18). */
export type PaymentDrawerFieldKey = 'amount' | 'method' | 'receivedAt';

export interface PaymentDrawerFieldError {
  readonly field: PaymentDrawerFieldKey;
  readonly message: string;
}

// ---- Prefill (from the ONE handoff read; nothing recomputed) --------------

export interface PaymentDrawerPrefill {
  readonly form: PaymentDrawerForm;
  /** TRUE when the server supplied a lawful whole-rupee suggestion. */
  readonly amountPrefilled: boolean;
}

/**
 * Seed the form from the handoff wire. `nowLocal` is injected (deterministic
 * tests; the caller passes the same local-input value the billing page uses).
 * Payment method is deliberately NOT guessed -- it is the operator's single
 * required decision.
 */
export function derivePaymentDrawerPrefill(
  handoff: CheckoutHandoffResponse,
  nowLocal: string,
): PaymentDrawerPrefill {
  const billDue = collectionOutstandingMinorUnits(handoff);
  const suggested = (handoff.finalizedBills?.billCount ?? 0) > 0
    ? billDue === null ? null : billDue / 100
    : handoff.doorSuggestedAmountInr;
  const amountPrefilled = suggested !== null && suggested > 0;
  return {
    form: {
      amountRaw: amountPrefilled ? String(suggested) : '',
      method: '',
      receivedAtLocal: nowLocal,
      // NEVER guessed and never carried over from a previous collection --
      // a reference belongs to exactly one transaction.
      referenceRaw: '',
    },
    amountPrefilled,
  };
}

// ---- Header facts (short labeled values; no prose, no recomputation) ------

export interface PaymentDrawerOutstanding {
  /** TRUE when the value is a real money figure; FALSE = an honest state. */
  readonly isAmount: boolean;
  readonly text: string;
}

/**
 * The headline money value: the server's OUTSTANDING when it IS a number,
 * otherwise the episode state's OWN honest token (the existing billing
 * vocabulary, reused -- never Rs 0, never a fabricated total).
 *
 * VOCABULARY NOTE: this is deliberately "Outstanding" (the billing queue's
 * episode-grain word), never "Amount due" / "Balance due". Those belong to the
 * ACCOUNT grain, which does not exist yet (gap.patient-account-model), and are
 * pinned FORBIDDEN across this codebase -- see the account-honesty pins in
 * tests/unit/checkout-handoff-route.test.ts and tests/unit/doctor-patient-lens.test.ts.
 */
export function paymentDrawerOutstanding(
  handoff: CheckoutHandoffResponse,
): PaymentDrawerOutstanding {
  const bill=handoff.finalizedBills;
  if(bill && bill.billCount>0) return bill.state==='known' && bill.dueMinorUnits!==null
    ? {isAmount:true,text:formatMinorUnitsInr(bill.dueMinorUnits)}
    : {isAmount:false,text:bill.state==='allocation_unknown'?'Bill allocation unknown':'Bill balance unavailable'};
  if (
    handoff.queueState === 'outstanding' &&
    handoff.outstandingMinorUnits !== null
  ) {
    return {
      isAmount: true,
      text: formatMinorUnitsInr(handoff.outstandingMinorUnits),
    };
  }
  if (handoff.queueState === 'not_configured') {
    return { isAmount: false, text: ACCEPTED_AMOUNT_NOT_CONFIGURED_COPY };
  }
  return {
    isAmount: false,
    text: episodeStateLabel(handoff.queueState as BillingQueueEpisodeState),
  };
}

// ---- Validation (field-level; entered data is NEVER discarded) ------------

export interface PaymentDrawerValidation {
  readonly ok: boolean;
  readonly errors: readonly PaymentDrawerFieldError[];
  /** Non-null only when ok. */
  readonly amountMinorUnits: number | null;
  /** Non-null only when ok. */
  readonly receivedAtIso: string | null;
}

/** Short, actionable, one per field. */
export const PAYMENT_DRAWER_AMOUNT_ERROR =
  'Enter an amount (up to 2 decimals).';
export const PAYMENT_DRAWER_METHOD_ERROR = 'Pick a method.';
export const PAYMENT_DRAWER_RECEIVED_AT_ERROR = 'Enter a valid time.';

/**
 * Validate every field and return EVERY failure at once, each anchored to its
 * own field -- the operator fixes one pass, not one error per submit. Pure:
 * the caller keeps its form state untouched, so entered data survives every
 * failure (including a server error, which never clears the form).
 */
export function validatePaymentDrawerForm(
  form: PaymentDrawerForm,
): PaymentDrawerValidation {
  const errors: PaymentDrawerFieldError[] = [];

  const amountMinorUnits = parseInrToMinorUnits(form.amountRaw);
  if (amountMinorUnits === null) {
    errors.push({ field: 'amount', message: PAYMENT_DRAWER_AMOUNT_ERROR });
  }
  const methodError = paymentMethodDetailsError({method:form.method, methodSubtype:form.methodSubtype||null, methodOtherName:form.methodOtherName?.trim()||null, referenceNumber:form.method==='cash'?null:referenceNumberForWire(form.referenceRaw)});
  if (methodError) errors.push({field:'method', message:form.method ? methodError : PAYMENT_DRAWER_METHOD_ERROR});
  const receivedAtIso = occurredAtIsoFromLocalInput(form.receivedAtLocal);
  if (receivedAtIso === null) {
    errors.push({
      field: 'receivedAt',
      message: PAYMENT_DRAWER_RECEIVED_AT_ERROR,
    });
  }

  const ok = errors.length === 0;
  return {
    ok,
    errors,
    amountMinorUnits: ok ? amountMinorUnits : null,
    receivedAtIso: ok ? receivedAtIso : null,
  };
}

/** The message for ONE field, or null. Drives the field-anchored render. */
export function fieldErrorFor(
  errors: readonly PaymentDrawerFieldError[],
  field: PaymentDrawerFieldKey,
): string | null {
  return errors.find((e) => e.field === field)?.message ?? null;
}

// ---- The submit state machine + idempotency arming -----------------------

/**
 * 'idle'       -- editable, submittable.
 * 'submitting' -- in flight; the primary action is disabled AND the guard
 *                 below refuses a second entry (double-submit is impossible
 *                 even if the button is driven programmatically).
 * 'error'      -- a failed attempt; the form is intact and RETRYABLE with the
 *                 SAME requestId, so a retry can never mint a second payment.
 * 'recorded'   -- TERMINAL. The receipt has replaced the form, so the surface
 *                 can never look ready-to-resubmit.
 */
export type PaymentDrawerSubmitState =
  | 'idle'
  | 'submitting'
  | 'error'
  | 'recorded';

/** The ONLY states a submission may start from. */
export function canSubmitPaymentDrawer(
  state: PaymentDrawerSubmitState,
): boolean {
  return state === 'idle' || state === 'error';
}

/**
 * ONE arming per visit. The requestId is minted when the drawer ARMS and is
 * REUSED for every retry of that submission, so the writer's
 * UNIQUE(organization_id, request_id) collapses a double-submit to ONE row
 * and ONE audit row (the replay returns created:false). A NEW patient/visit
 * arm mints a FRESH requestId.
 */
export interface PaymentDrawerArm {
  /** The episode, VERBATIM from the wire (LES-022). */
  readonly appointmentId: number;
  readonly clinicId: number;
  readonly patientId: string | null;
  readonly requestId: string;
  /** Monotonic arm token -- the stale-response guard. */
  readonly seq: number;
}

/**
 * STALE-ARM GUARD: a response belongs to the CURRENT arming only when its arm
 * token still matches. Rapid Collect clicks across two rows can therefore
 * never marry visit A's response to visit B's drawer (the adversarial S3/S4
 * race the billing page already guards; the same law, one home).
 */
export function isCurrentArm(
  current: PaymentDrawerArm | null,
  responseSeq: number,
): boolean {
  return current !== null && current.seq === responseSeq;
}

/**
 * TRUE when a fresh requestId must be minted: a different episode, a
 * different patient, or no prior arm. Re-opening the SAME visit's drawer
 * after a failure keeps the id (the retry stays idempotent).
 */
export function needsFreshRequestId(
  previous: PaymentDrawerArm | null,
  next: { readonly appointmentId: number; readonly patientId: string | null },
): boolean {
  if (previous === null) return true;
  return (
    previous.appointmentId !== next.appointmentId ||
    previous.patientId !== next.patientId
  );
}

/**
 * The drawer can only submit when the server said the caller may record a
 * payment here AND the episode carries the identity the visit-anchored writer
 * requires. FALSE removes the primary action ENTIRELY (absent, never
 * rendered-but-disabled, never a 401ing button).
 */
export function paymentDrawerActionAvailable(
  handoff: CheckoutHandoffResponse,
): boolean {
  const due = collectionOutstandingMinorUnits(handoff);
  return handoff.canRecordPayment === true && handoff.patientId !== null
    && due !== null && Number.isSafeInteger(due) && due > 0;
}


/** Explain the actual unavailable boundary without inventing a role refusal. */
export function paymentDrawerUnavailableReason(handoff: CheckoutHandoffResponse): string | null {
  if (handoff.canRecordPayment !== true) return 'Recording is not available for your role here.';
  if (handoff.patientId === null) return 'This visit has no verified patient link. Resolve the patient link before recording.';
  return null;
}
